import {
  STAMINA_MAX,
  TICK_DT,
  eyeHeightFor,
  stepMovement,
  type CollisionWorld,
  type InputCmd,
  type MoveMods,
  type MoveState,
  type Snapshot,
  type Vec3,
} from "@rietz/shared";

/**
 * Client-side prediction and reconciliation. The client is never authoritative:
 * every snapshot rebases onto `snap.you` and replays the unacknowledged
 * commands through the same `stepMovement` at the same `TICK_DT`; the only
 * thing kept is a VISUAL offset eased out over ~100 ms. Three regimes by replay
 * disagreement: < 1 mm untouched; < 0.5 m accepted with a decaying render
 * offset; ≥ 0.5 m a hard snap.
 */

/** Commands kept for replay. 128 ticks ≈ 4.2 s at 30 Hz. */
const BUFFER_SIZE = 128;

/** Below this the replay is considered identical to what was predicted. */
const ERR_IGNORE_M = 0.001;
/** At or above this the correction is applied instantly, with no smoothing. */
const ERR_SNAP_M = 0.5;

/** Time constant of the render-offset decay, ms — ~95 % gone after 100 ms. */
const SMOOTH_TAU_MS = 100 / 3;

/** Once the visual offset is this small it is simply zeroed. */
const SMOOTH_EPS_M = 1e-4;

interface Entry {
  cmd: InputCmd;
  state: MoveState;
}

function cloneState(s: MoveState): MoveState {
  return {
    pos: { x: s.pos.x, y: s.pos.y, z: s.pos.z },
    vel: { x: s.vel.x, y: s.vel.y, z: s.vel.z },
    onGround: s.onGround,
    stance: s.stance,
    stamina: s.stamina,
  };
}

export class Prediction {
  /** Metres between the last replay and what had been predicted. */
  lastErrorM = 0;
  /** How the last correction was handled — surfaced in the debug overlay. */
  lastCorrection: "none" | "ignored" | "smoothed" | "snapped" = "none";
  /** Number of commands still awaiting acknowledgement. */
  get pendingCmds(): number {
    return this.count;
  }

  private world: CollisionWorld;

  /** Authoritative-as-far-as-we-know state after the newest local command. */
  private curr: MoveState;
  /** State after the previous command — used to interpolate inside a tick. */
  private prev: MoveState;

  /** Purely visual; never fed back into the simulation. */
  private readonly smooth: Vec3 = { x: 0, y: 0, z: 0 };

  /**
   * The movement modifiers this client believes the server applies. Mutated in
   * place so nothing allocates; starts unencumbered and uninjured.
   */
  private readonly mods: MoveMods = { speedMult: 1, accelMult: 1 };

  private readonly buf: (Entry | undefined)[] = new Array<Entry | undefined>(
    BUFFER_SIZE,
  ).fill(undefined);
  /** Index one past the newest entry. */
  private head = 0;
  private count = 0;

  constructor(world: CollisionWorld, spawn: Vec3) {
    this.world = world;
    this.curr = spawnState(spawn);
    this.prev = cloneState(this.curr);
  }

  /** Swaps the collision world (map change) and teleports to `spawn`. */
  respawn(world: CollisionWorld, spawn: Vec3): void {
    this.world = world;
    this.curr = spawnState(spawn);
    this.prev = cloneState(this.curr);
    this.clearBuffer();
    this.smooth.x = 0;
    this.smooth.y = 0;
    this.smooth.z = 0;
    this.lastErrorM = 0;
    this.lastCorrection = "none";
  }

  /**
   * Swaps the collision world UNDER the player without moving them — the
   * hideout rebuilding around them. `respawn` would be a visible jump for
   * nothing; here the next `stepMovement` collides against the new geometry and
   * `resolveCapsule` pushes the player out of whatever appeared. The
   * unacknowledged tail is kept; there is no server to disagree.
   */
  setWorld(world: CollisionWorld): void {
    this.world = world;
  }

  get state(): Readonly<MoveState> {
    return this.curr;
  }

  /**
   * Tells the prediction how encumbered and injured we think we are. A BELIEF,
   * not authority: the replay uses whatever it was last told, including for
   * ticks the server simulated under different mods — a small disagreement
   * `onSnapshot` already smooths. Doing better would cost the property that the
   * replay is a pure function of server state plus commands. Copied fieldwise.
   */
  setMods(mods: MoveMods): void {
    this.mods.speedMult = mods.speedMult;
    this.mods.accelMult = mods.accelMult;
  }

  /** Steps the local simulation by one command and records it for replay. */
  applyLocalCmd(cmd: InputCmd): Readonly<MoveState> {
    this.prev = this.curr;
    const next = stepMovement(
      this.curr,
      cmd,
      this.world,
      this.mods,
      TICK_DT,
    );
    this.curr = next;
    this.push({ cmd, state: next });
    return next;
  }

  /**
   * Rebases onto the server's authoritative state and replays everything it has
   * not seen yet.
   */
  onSnapshot(snap: Snapshot): void {
    const predicted = this.curr;
    /** The interpolation basis in use this frame — preserved across rebases. */
    const prevRender = this.prev;

    // 1. Discard commands the server has already consumed.
    this.dropAcked(snap.lastAckSeq);

    // 2. Start from the server's truth.
    let replayed: MoveState = {
      pos: { x: snap.you.pos.x, y: snap.you.pos.y, z: snap.you.pos.z },
      vel: { x: snap.you.vel.x, y: snap.you.vel.y, z: snap.you.vel.z },
      onGround: snap.you.onGround,
      stance: snap.you.stance,
      stamina: snap.you.stamina,
    };

    // 3. Replay the unacknowledged tail, rewriting each entry's stored result
    //    so a later, older snapshot still reconciles against correct history.
    for (let i = 0; i < this.count; i++) {
      const entry = this.at(i);
      if (entry === undefined) continue;
      replayed = stepMovement(
        replayed,
        entry.cmd,
        this.world,
        this.mods,
        TICK_DT,
      );
      entry.state = replayed;
    }

    // 4. Compare and decide how visible the correction is allowed to be.
    const dx = predicted.pos.x - replayed.pos.x;
    const dy = predicted.pos.y - replayed.pos.y;
    const dz = predicted.pos.z - replayed.pos.z;
    const err = Math.sqrt(dx * dx + dy * dy + dz * dz);
    this.lastErrorM = err;

    if (err >= ERR_SNAP_M) {
      // The client was badly wrong; a visible teleport is the point.
      this.lastCorrection = "snapped";
      this.smooth.x = 0;
      this.smooth.y = 0;
      this.smooth.z = 0;
      this.curr = replayed;
      this.prev = replayed;
      return;
    }

    if (err < ERR_IGNORE_M) {
      // Agreement. Still adopt the replayed state: it carries the server's
      // stamina/onGround/stance, which the position alone does not.
      this.lastCorrection = "ignored";
    } else {
      this.lastCorrection = "smoothed";
      this.smooth.x += dx;
      this.smooth.y += dy;
      this.smooth.z += dz;
    }

    // Adopt the replayed state WITHOUT collapsing the render-interpolation
    // pair:
    // `prev = curr` would freeze the rendered position for the rest of the tick
    // and then jump — a 15 Hz judder. Carry the old `prev` forward shifted by
    // the
    // same correction; real error is worked off by the smoothing offset.
    this.curr = replayed;
    this.prev = {
      pos: {
        x: prevRender.pos.x - dx,
        y: prevRender.pos.y - dy,
        z: prevRender.pos.z - dz,
      },
      vel: { x: prevRender.vel.x, y: prevRender.vel.y, z: prevRender.vel.z },
      onGround: prevRender.onGround,
      stance: prevRender.stance,
      stamina: prevRender.stamina,
    };
  }

  /** Decays the visual correction offset. Call once per rendered frame. */
  decaySmoothing(dtMs: number): void {
    if (
      this.smooth.x === 0 &&
      this.smooth.y === 0 &&
      this.smooth.z === 0
    ) {
      return;
    }
    const k = Math.exp(-dtMs / SMOOTH_TAU_MS);
    this.smooth.x *= k;
    this.smooth.y *= k;
    this.smooth.z *= k;
    if (
      Math.abs(this.smooth.x) < SMOOTH_EPS_M &&
      Math.abs(this.smooth.y) < SMOOTH_EPS_M &&
      Math.abs(this.smooth.z) < SMOOTH_EPS_M
    ) {
      this.smooth.x = 0;
      this.smooth.y = 0;
      this.smooth.z = 0;
    }
  }

  /** Current magnitude of the un-decayed visual correction, metres. */
  get smoothingMagnitudeM(): number {
    const { x, y, z } = this.smooth;
    return Math.sqrt(x * x + y * y + z * z);
  }

  /**
   * Feet position to render at, including the correction offset.
   * `alpha` is the fraction of the current tick already elapsed.
   */
  sampleRenderPos(alpha: number, out: Vec3): Vec3 {
    const a = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    const p = this.prev.pos;
    const c = this.curr.pos;
    out.x = p.x + (c.x - p.x) * a + this.smooth.x;
    out.y = p.y + (c.y - p.y) * a + this.smooth.y;
    out.z = p.z + (c.z - p.z) * a + this.smooth.z;
    return out;
  }

  /** Eye height to render at, eased across a stance change. */
  sampleRenderEyeHeight(alpha: number): number {
    const a = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    const from = eyeHeightFor(this.prev.stance);
    const to = eyeHeightFor(this.curr.stance);
    return from + (to - from) * a;
  }

  // ---------------------------------------------------------------------
  // Ring buffer.
  // ---------------------------------------------------------------------

  /** `i = 0` is the oldest retained entry. */
  private at(i: number): Entry | undefined {
    const idx = (this.head - this.count + i + BUFFER_SIZE * 2) % BUFFER_SIZE;
    return this.buf[idx];
  }

  private push(e: Entry): void {
    this.buf[this.head] = e;
    this.head = (this.head + 1) % BUFFER_SIZE;
    if (this.count < BUFFER_SIZE) this.count += 1;
  }

  private dropAcked(lastAckSeq: number): void {
    let drop = 0;
    while (drop < this.count) {
      const e = this.at(drop);
      if (e === undefined || e.cmd.seq > lastAckSeq) break;
      drop += 1;
    }
    this.count -= drop;
  }

  private clearBuffer(): void {
    this.buf.fill(undefined);
    this.head = 0;
    this.count = 0;
  }
}

function spawnState(spawn: Vec3): MoveState {
  return {
    pos: { x: spawn.x, y: spawn.y, z: spawn.z },
    vel: { x: 0, y: 0, z: 0 },
    onGround: false,
    stance: "stand",
    stamina: STAMINA_MAX,
  };
}
